import { useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { adminApi } from '@/lib/api'
import { StatsCard } from '@/components/stats-card'
import { formatDate, formatCurrency } from '@/lib/utils'
import {
  ArrowLeft,
  Calendar,
  CheckCircle,
  Users,
  ImageIcon,
  Wallet,
  ExternalLink,
} from 'lucide-react'
import type { AssignPlanPayload } from '@/types/admin'

export default function TenantDetailPage() {
  const { id } = useParams<{ id: string }>()
  const qc = useQueryClient()
  const [showPlanModal, setShowPlanModal] = useState(false)

  const { data: tenant, isLoading } = useQuery({
    queryKey: ['admin', 'tenants', id],
    queryFn: () => adminApi.getTenant(id!),
    enabled: !!id,
  })

  const { data: plans = [] } = useQuery({
    queryKey: ['admin', 'plans'],
    queryFn: adminApi.getPlans,
  })

  const toggleActive = useMutation({
    mutationFn: (is_active: boolean) => adminApi.updateTenant(id!, { is_active }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'tenants', id] })
      qc.invalidateQueries({ queryKey: ['admin', 'tenants'] })
    },
  })

  const assignPlan = useMutation({
    mutationFn: (data: AssignPlanPayload) => adminApi.assignPlan(id!, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'tenants', id] })
      setShowPlanModal(false)
    },
  })

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="h-6 w-48 animate-pulse rounded bg-muted" />
        <div className="grid grid-cols-2 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-24 animate-pulse rounded-lg border bg-muted" />
          ))}
        </div>
      </div>
    )
  }

  if (!tenant) {
    return (
      <div className="space-y-4">
        <Link to="/tenants" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" /> Kembali
        </Link>
        <p className="text-sm text-destructive">Tenant tidak ditemukan.</p>
      </div>
    )
  }

  const sub = tenant.tenant_subscriptions
  const currentPlanId = sub?.plans?.id ?? ''

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Link
          to="/tenants"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          Semua Tenant
        </Link>
      </div>

      <div className="flex items-start justify-between">
        <div className="flex items-center gap-4">
          {tenant.logo_url ? (
            <img src={tenant.logo_url} alt="" className="h-14 w-14 rounded-full object-cover" />
          ) : (
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-muted text-xl font-bold">
              {tenant.name[0]}
            </div>
          )}
          <div>
            <h1 className="text-2xl font-bold">{tenant.name}</h1>
            <p className="font-mono text-sm text-muted-foreground">{tenant.slug}.oneidol.id</p>
            {tenant.description && (
              <p className="mt-1 text-sm text-muted-foreground">{tenant.description}</p>
            )}
          </div>
        </div>

        <div className="flex gap-2">
          <a
            href={`https://${tenant.slug}.oneidol.id`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 rounded-md border border-input px-3 py-1.5 text-sm hover:bg-muted"
          >
            <ExternalLink className="h-3.5 w-3.5" />
            Lihat Halaman
          </a>
          <button
            onClick={() => toggleActive.mutate(!tenant.is_active)}
            disabled={toggleActive.isPending}
            className={`rounded-md px-3 py-1.5 text-sm font-medium disabled:opacity-50 ${
              tenant.is_active
                ? 'bg-destructive/10 text-destructive hover:bg-destructive/20'
                : 'bg-green-100 text-green-700 hover:bg-green-200'
            }`}
          >
            {tenant.is_active ? 'Nonaktifkan' : 'Aktifkan'}
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatsCard
          title="Total Event"
          value={tenant.stats.total_events}
          icon={Calendar}
        />
        <StatsCard
          title="Total Member"
          value={tenant.stats.total_members}
          icon={Users}
        />
        <StatsCard
          title="Booking Verified"
          value={tenant.stats.total_verified_bookings}
          icon={CheckCircle}
        />
        <StatsCard
          title="Total Revenue"
          value={formatCurrency(tenant.stats.total_revenue_idr)}
          icon={Wallet}
        />
      </div>

      {/* Subscription */}
      <div className="rounded-lg border bg-card p-5">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-semibold">Langganan & Plan</h2>
          <button
            onClick={() => setShowPlanModal(true)}
            className="rounded-md bg-primary px-3 py-1.5 text-sm text-primary-foreground hover:bg-primary/90"
          >
            Ganti Plan
          </button>
        </div>

        {sub ? (
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-muted-foreground">Plan</p>
              <p className="font-medium">{sub.plans?.name ?? '—'}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Status Langganan</p>
              <p className="font-medium capitalize">{sub.status}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Mulai</p>
              <p className="font-medium">{formatDate(sub.started_at)}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Berakhir</p>
              <p className="font-medium">
                {sub.expires_at ? formatDate(sub.expires_at) : 'Tidak terbatas'}
              </p>
            </div>
            {sub.notes && (
              <div className="col-span-2">
                <p className="text-muted-foreground">Catatan</p>
                <p className="font-medium">{sub.notes}</p>
              </div>
            )}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">Belum ada langganan aktif.</p>
        )}
      </div>

      {/* Info */}
      <div className="rounded-lg border bg-card p-5">
        <h2 className="mb-4 font-semibold">Informasi Tenant</h2>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-muted-foreground">Status</p>
            <p className="font-medium">{tenant.is_active ? 'Aktif' : 'Nonaktif'}</p>
          </div>
          <div>
            <p className="text-muted-foreground">Dibuat</p>
            <p className="font-medium">{formatDate(tenant.created_at)}</p>
          </div>
          {tenant.logo_url && (
            <div>
              <p className="text-muted-foreground">Logo</p>
              <a
                href={tenant.logo_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-blue-600 hover:underline"
              >
                <ImageIcon className="h-3.5 w-3.5" />
                Lihat
              </a>
            </div>
          )}
        </div>
      </div>

      {/* Plan Modal */}
      {showPlanModal && (
        <AssignPlanModal
          plans={plans}
          currentPlanId={currentPlanId}
          currentStatus={sub?.status ?? 'active'}
          currentNotes={sub?.notes ?? ''}
          onClose={() => setShowPlanModal(false)}
          onSubmit={(data) => assignPlan.mutate(data)}
          isPending={assignPlan.isPending}
          error={assignPlan.error?.message}
        />
      )}
    </div>
  )
}

function AssignPlanModal({
  plans,
  currentPlanId,
  currentStatus,
  currentNotes,
  onClose,
  onSubmit,
  isPending,
  error,
}: {
  plans: Array<{ id: string; name: string; slug: string }>
  currentPlanId: string
  currentStatus: string
  currentNotes: string
  onClose: () => void
  onSubmit: (data: AssignPlanPayload) => void
  isPending: boolean
  error?: string
}) {
  const [planId, setPlanId] = useState(currentPlanId)
  const [status, setStatus] = useState<AssignPlanPayload['status']>(
    (currentStatus as AssignPlanPayload['status']) ?? 'active'
  )
  const [notes, setNotes] = useState(currentNotes)
  const [expiresAt, setExpiresAt] = useState('')

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    onSubmit({
      plan_id: planId,
      status,
      expires_at: expiresAt || null,
      notes: notes || undefined,
    })
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-md rounded-lg border bg-card p-6 shadow-lg">
        <h2 className="mb-4 text-lg font-semibold">Ganti Plan Tenant</h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <label className="text-sm font-medium">Plan</label>
            <select
              value={planId}
              onChange={(e) => setPlanId(e.target.value)}
              required
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <option value="">Pilih plan...</option>
              {plans.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-medium">Status Langganan</label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value as AssignPlanPayload['status'])}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <option value="active">Aktif</option>
              <option value="suspended">Suspended</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-medium">Berakhir Pada (opsional)</label>
            <input
              type="date"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
            <p className="text-xs text-muted-foreground">Kosongkan jika tidak ada batas waktu.</p>
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-medium">Catatan Internal</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              placeholder="Catatan untuk keperluan internal..."
            />
          </div>

          {error && (
            <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</p>
          )}

          <div className="flex gap-3">
            <button
              type="submit"
              disabled={isPending || !planId}
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {isPending ? 'Menyimpan...' : 'Simpan'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-input px-4 py-2 text-sm hover:bg-muted"
            >
              Batal
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
